/**
 * Flow steps store one model reference, `"provider:model"`
 * (`google:gemini-2.5-flash`, `microsoft-ocr:prebuilt-layout`); the prefix
 * selects the registry credential. The admin edits it as a provider picker plus
 * a model field, so it is split on load and joined on save.
 *
 * Prefix -> catalog provider key. Mirrors `PROVIDER_PREFIXES` of
 * `xplatform_providers.resolver`; the catalog keys are themselves valid prefixes.
 */
const PROVIDER_PREFIXES: Record<string, string> = {
  google: "google",
  gemini: "google",
  azure: "microsoft",
  microsoft: "microsoft",
  bedrock: "aws",
  aws: "aws",
  "document-intelligence": "microsoft-ocr",
  "microsoft-ocr": "microsoft-ocr",
  "azure-ocr": "microsoft-ocr",
  openai: "openai",
};

export interface ModelRefParts {
  /** Catalog provider key; empty for a bare legacy alias. */
  provider: string;
  model: string;
  /** The prefix as written (`azure`, `document-intelligence`); empty for a bare alias. */
  prefix: string;
}

/**
 * Split a reference. Only the first `:` separates (Bedrock ids carry their own,
 * `...-v1:0`). A value without a known prefix is a bare registry alias: it is
 * kept whole as the model, with no provider.
 */
export const splitModelRef = (ref: string | null | undefined): ModelRefParts => {
  const value = (ref ?? "").trim();
  const separator = value.indexOf(":");
  if (separator > 0) {
    const prefix = value.slice(0, separator).trim().toLowerCase();
    const provider = PROVIDER_PREFIXES[prefix];
    if (provider) return { provider, model: value.slice(separator + 1).trim(), prefix };
  }
  return { provider: "", model: value, prefix: "" };
};

/**
 * Join a provider and a model into a reference. A model already written as a
 * reference is kept as typed; without a provider the bare model is kept.
 * `writtenPrefix` (the prefix the stored reference used) is reused while it
 * still names the same provider, so saving never rewrites an untouched
 * reference (`azure:` stays `azure:`, not `microsoft:`).
 */
export const joinModelRef = (
  provider: string | null | undefined,
  model: string | null | undefined,
  writtenPrefix?: string | null,
): string => {
  const name = (model ?? "").trim();
  if (!name) return "";
  if (splitModelRef(name).provider) return name;
  if (!provider) return name;
  const prefix = writtenPrefix && PROVIDER_PREFIXES[writtenPrefix] === provider ? writtenPrefix : provider;
  return `${prefix}:${name}`;
};
